import InitApi from "api/InitApi";
import ServerErrors from "components/SeverErrors";
import toast from "react-hot-toast";
import { useMutation } from "react-query";
import { useHistory } from "react-router-dom";
import { reactLocalStorage } from "reactjs-localstorage";
import useBooksFilterStore from "store/booksFilterStore";
import useTokenStore from "store/tokenStore";

/**
 * Helper: extract a filename from the Content-Disposition header.
 * Falls back to the provided default when the header is absent.
 */
const getFilenameFromResponse = (response, fallback = "books_collection.csv") => {
	const disposition = response.headers?.["content-disposition"] || "";
	const match = disposition.match(/filename[^;=\n]*=["']?([^"';\n]+)/i);
	return match?.[1] || fallback;
};

const useExportBooks = () => {
	const { accessToken } = useTokenStore();

	const { bookFilters } = useBooksFilterStore();

	const history = useHistory();

	/**
	 * Build request body for export from filters.
	 * @param {Object} filters - AllBooks2-style filters (categoryIs, genreIs, etc.)
	 * @returns {Object} API request body
	 */
	const buildExportPayload = (filters) => {
		if (!filters) {
			return {
				format: "csv",
				"filter[isFree]": bookFilters["filter[isFree]"],
				"filter[is_active]": bookFilters["filter[is_active]"],
				"filter[category_is]": bookFilters["filter[category_is]"],
				"filter[genre_is]": bookFilters["filter[genre_is]"],
				"filter[publisher_is]": bookFilters["filter[publisher_is]"],
				"filter[pricing_model_is]": bookFilters["filter[pricing_model_is]"],
				searchTerm: bookFilters.searchTerm,
				without_file_collection: bookFilters["without_file_collection"],
				with_file_collection: bookFilters["with_file_collection"],
			};
		}
		return {
			"filter[isFree]": filters.isFree || "",
			"filter[is_active]": filters.isActive || "",
			"filter[category_is]": filters.categoryIs || "",
			"filter[genre_is]": filters.genreIs || "",
			"filter[publisher_is]": filters.publisherIs || "",
			"filter[pricing_model_is]": filters.pricingModelIs || "",
			searchTerm: filters.search || "",
			without_file_collection: filters.withoutFileCollection || "",
			with_file_collection: filters.withFileCollection || "",
		};
	};

	/**
	 * Export books.
	 * @param {string|Object} arg - "csv"|"xlsx" or { format: "csv"|"xlsx", filters?: Object }
	 */
	const exportBooks = async (arg) => {
		const isObject = typeof arg === "object" && arg !== null;
		const format = isObject ? arg.format ?? "csv" : arg ?? "csv";
		const filters = isObject ? arg.filters : null;

		const payload = buildExportPayload(filters);
		payload.format = format;

		const instance = await InitApi({ accessToken });

		const response = await instance.post(
			"/books/export",
			payload,
			{
				responseType: "blob",
			}
		);

		// Derive MIME type and filename from the server response
		const contentType = response.headers?.["content-type"] || "text/csv";
		const filename = getFilenameFromResponse(
			response,
			format === "xlsx" ? "books_collection.xlsx" : "books_collection.csv"
		);

		// Create blob and trigger browser download
		const blob = new Blob([response.data], { type: contentType });
		const url = window.URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.href = url;
		link.download = filename;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		window.URL.revokeObjectURL(url);

		return response;
	};

	return useMutation(exportBooks, {
		onSuccess: async () => {
			toast.success("Books metadata downloaded successfully");
		},
		onError: async (error) => {
			// When the backend returns a JSON error (e.g. 422 for XLSX threshold)
			// but we requested a blob, we need to parse the blob back to JSON.
			let errorMessage = "Failed to download books metadata";
			let errorData = null;

			if (error?.response?.data instanceof Blob) {
				try {
					const text = await error.response.data.text();
					const json = JSON.parse(text);
					errorMessage = json?.message || errorMessage;
					errorData = json?.data || null;
				} catch {
					// blob wasn't JSON — keep defaults
				}
			} else {
				errorMessage = error?.response?.data?.message || errorMessage;
				errorData = error?.response?.data?.data || null;
			}

			toast.error(
				<ServerErrors errorMessage={errorMessage} errors={errorData} />,
				{
					className: "max-w-max",
				}
			);

			if (errorMessage === "Unauthenticated.") {
				reactLocalStorage.clear();
				history.push("/");
			}
		},
	});
};

export default useExportBooks;
